import React, { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from '../../../ui/toast';
import LogoWhite from '../../../assets/images/logos/white-bg-logo-hiacuity.png';
import { useGetPublicCCAConfigQuery } from '../../../utils/api_service/ccaApi';
import {
  useGetPreQualFormQuery,
  useGetPreQualVideoPresignMutation,
  useGetPublicPreQualVideoPresignMutation,
  useSubmitPreQualMutation,
  useSubmitPublicPreQualMutation,
} from '../../../utils/api_service/preQualApi';
import { awsUploadVideo } from '../../../utils/api_service/storage_api';
import { LocationPicker } from '../viewJobPosting/JobPostDetails/components/LocationPicker';

type VideoAnswerValue = {
  file: File;
  previewUrl: string;
};

type VideoQuestionInputProps = {
  value?: VideoAnswerValue;
  onChange: (value: VideoAnswerValue) => void;
  disabled?: boolean;
};

type RecordingMode = 'idle' | 'camera' | 'recording' | 'processing';

const isVideoQuestionType = (questionType?: string) =>
  questionType === 'video-answer' || questionType === 'video';

const getApiErrorMessage = (error: any) => {
  const detail = error?.data?.detail || error?.data?.message || error?.error;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .join(', ');
  }
  return typeof detail === 'string' ? detail : '';
};

const VideoQuestionInput = ({
  value,
  onChange,
  disabled,
}: VideoQuestionInputProps) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const chunksRef = React.useRef<Blob[]>([]);
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const [mode, setMode] = React.useState<RecordingMode>('idle');
  const [cameraError, setCameraError] = React.useState<string | null>(null);
  const [recordingSecs, setRecordingSecs] = React.useState(0);

  // Recording timer — starts/stops with mode
  React.useEffect(() => {
    if (mode === 'recording') {
      setRecordingSecs(0);
      timerRef.current = setInterval(() => setRecordingSecs((s) => s + 1), 1000);
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  }, [mode]);

  const formatRecordingTime = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Stop and release the live camera stream
  const stopStream = React.useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    // Clear srcObject on the video element to fully release the camera
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => { stopStream(); };
  }, [stopStream]);

  // Attach stream to video element whenever mode switches to camera/recording
  React.useEffect(() => {
    if ((mode === 'camera' || mode === 'recording') && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {});
    }
  }, [mode]);

  const openCamera = async () => {
    try {
      setCameraError(null);
      if (!streamRef.current) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        streamRef.current = stream;
      }
      setMode('camera');
    } catch {
      setCameraError('Camera access failed. You can upload a recorded video instead.');
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const rec = new MediaRecorder(streamRef.current);
    mediaRecorderRef.current = rec;
    rec.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const file = new File([blob], `prequal-${Date.now()}.webm`, { type: 'video/webm' });
      stopStream(); // Release camera before showing playback
      onChange({ file, previewUrl: URL.createObjectURL(blob) });
      setMode('idle');
    };
    rec.start();
    setMode('recording');
  };

  const stopRecording = () => {
    // Switch to processing IMMEDIATELY so the live feed disappears right away
    setMode('processing');
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
  };

  const handleRecordAgain = () => {
    stopStream();
    setMode('idle');
    // Re-open the camera preview right away
    openCamera();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    stopStream();
    setMode('idle');
    onChange({ file, previewUrl: URL.createObjectURL(file) });
  };

  // Derived booleans for clarity
  const showPlayback = !!value?.previewUrl && mode === 'idle';
  const showLiveCamera = mode === 'camera' || mode === 'recording';

  return (
    <div className='space-y-3'>
      { /* ── Video viewport ── */ }
      <div className='relative rounded-xl border border-slate-200 bg-slate-950 overflow-hidden aspect-video max-w-md'>

        { /* Recorded / uploaded playback */ }
        { showPlayback && (
          <video
              key={value!.previewUrl}
              src={value!.previewUrl}
              controls
              className='w-full h-full object-contain bg-black'
          />
        ) }

        { /* Live camera feed (recording or preview-before-record) */ }
        { showLiveCamera && (
          <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className='w-full h-full object-cover'
          />
        ) }

        { /* Processing spinner — shown immediately on Stop so live feed disappears */ }
        { mode === 'processing' && (
          <div className='absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-950'>
            <div className='w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full animate-spin' />
            <p className='text-xs text-white/50 font-medium'>Processing recording…</p>
          </div>
        ) }

        { /* Idle placeholder — no recording yet */ }
        { mode === 'idle' && !value?.previewUrl && (
          <div className='absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-600'>
            <svg className='w-10 h-10 opacity-40' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={1.5}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z' />
            </svg>
            <p className='text-xs text-slate-500'>Record or upload a video response</p>
          </div>
        ) }

        { /* REC badge with live timer */ }
        { mode === 'recording' && (
          <div className='absolute top-2 left-2 flex items-center gap-1.5 bg-red-600/90 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow'>
            <div className='w-1.5 h-1.5 rounded-full bg-white animate-pulse' />
            REC
            <span className='font-mono tracking-wider'>{ formatRecordingTime(recordingSecs) }</span>
          </div>
        ) }
      </div>

      { cameraError && (
        <p className='text-xs font-semibold text-amber-600'>{ cameraError }</p>
      ) }

      { /* ── Controls ── */ }
      <div className='flex flex-wrap gap-2'>
        { showPlayback ? (
          /* After a recording/upload is ready */
          <>
            <button
                type='button'
                disabled={disabled}
                onClick={handleRecordAgain}
                className='px-4 py-2 rounded-lg text-xs font-bold text-white bg-emerald-600 disabled:opacity-60'
            >
              Record Again
            </button>
            <button
                type='button'
                disabled={disabled}
                onClick={() => fileInputRef.current?.click()}
                className='px-4 py-2 rounded-lg text-xs font-bold border border-slate-200 text-slate-700 bg-white disabled:opacity-60'
            >
              Upload Instead
            </button>
          </>
        ) : mode === 'camera' ? (
          /* Camera open, not yet recording */
          <button
              type='button'
              disabled={disabled}
              onClick={startRecording}
              className='flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white bg-red-600 disabled:opacity-60'
          >
            <div className='w-2 h-2 rounded-full bg-white animate-pulse' />
            Start Recording
          </button>
        ) : mode === 'recording' ? (
          /* Currently recording */
          <button
              type='button'
              disabled={disabled}
              onClick={stopRecording}
              className='flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold text-white bg-red-600 disabled:opacity-60'
          >
            <div className='w-2.5 h-2.5 rounded-sm bg-white' />
            Stop Recording
          </button>
        ) : mode === 'processing' ? (
          /* Waiting for MediaRecorder onstop */
          <button type='button' disabled className='px-4 py-2 rounded-lg text-xs font-bold text-white bg-slate-400 cursor-not-allowed'>
            Processing…
          </button>
        ) : (
          /* Idle — no recording yet */
          <>
            <button
                type='button'
                disabled={disabled}
                onClick={openCamera}
                className='px-4 py-2 rounded-lg text-xs font-bold text-white bg-emerald-600 disabled:opacity-60'
            >
              Record Video
            </button>
            <button
                type='button'
                disabled={disabled}
                onClick={() => fileInputRef.current?.click()}
                className='px-4 py-2 rounded-lg text-xs font-bold border border-slate-200 text-slate-700 bg-white disabled:opacity-60'
            >
              Upload Video
            </button>
          </>
        ) }
        <input
            ref={fileInputRef}
            type='file'
            accept='video/*'
            className='hidden'
            onChange={handleFileChange}
        />
      </div>
    </div>
  );
};

export const PreQualForm: React.FC = () => {
  const { jobOpeningId: pJobOpeningId } =
    useParams<{ jobOpeningId?: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token') || '';

  const getTokenPayload = (t: string) => {
    try {
      const base64Url = t.split('.')[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(window.atob(base64));
    } catch (e) {
      return null;
    }
  };

  const tokenPayload = getTokenPayload(token);
  const jobApplicantId =
    queryParams.get('jobApplicantId') ||
    queryParams.get('job_applicant_id') ||
    tokenPayload?.job_applicant_id ||
    tokenPayload?.jobApplicantId ||
    tokenPayload?.applicant_id ||
    '';

  // Decode jobOpeningId from token if not in params
  const getJobOpeningIdFromToken = () => {
    try {
      return tokenPayload?.job_opening_id || tokenPayload?.jobOpeningId || '';
    } catch (e) {
      return '';
    }
  };

  const jobOpeningId =
    pJobOpeningId ||
    queryParams.get('jobOpeningId') ||
    getJobOpeningIdFromToken();

  const { data, isLoading, error } = useGetPreQualFormQuery(jobOpeningId, {
    skip: !jobOpeningId,
  });

  const { data: ccaConfig } = useGetPublicCCAConfigQuery(
    { jobOpeningId, token },
    {
      skip: !jobOpeningId || !token,
    },
  );
  const ccaEnabled =
    !!ccaConfig &&
    ((ccaConfig.questions?.length ?? 0) > 0 ||
      (ccaConfig.quickConfig?.subdomains?.length ?? 0) > 0);

  const [submitPreQual, { isLoading: isSubmitting }] =
    useSubmitPreQualMutation();
  const [submitPublicPreQual, { isLoading: isSubmittingPublic }] =
    useSubmitPublicPreQualMutation();
  const [getVideoPresign] = useGetPreQualVideoPresignMutation();
  const [getPublicVideoPresign] = useGetPublicPreQualVideoPresignMutation();
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [locationDataMap, setLocationDataMap] = useState<Record<string, any>>(
    {},
  );
  const [isSubmitted, setIsSubmitted] = useState(false);

  const uploadVideoAndGetKey = async (questionKey: string, file: File) => {
    if (!jobApplicantId) {
      throw new Error('Missing job applicant id');
    }

    const presign = token
      ? await getVideoPresign({ questionKey, jobApplicantId, token }).unwrap()
      : await getPublicVideoPresign({
        questionKey,
        jobApplicantId,
      }).unwrap();

    if (!presign.url) {
      throw new Error('Missing upload URL');
    }

    // Create a blob without a MIME type to prevent the browser from automatically
    // sending a Content-Type header (which causes a 403 Forbidden signature mismatch on S3).
    const typelessBlob = new Blob([file]);
    const uploadResponse = await awsUploadVideo(presign.url, typelessBlob);
    if (!uploadResponse.ok) {
      throw new Error('Failed to upload video response');
    }

    return presign.videoKey || presign.url;
  };

  const handleInputChange = (
    questionKey: string,
    value: any,
    locData?: any,
  ) => {
    setAnswers((prev) => ({
      ...prev,
      [questionKey]: value,
    }));
    if (locData) {
      setLocationDataMap((prev) => ({
        ...prev,
        [questionKey]: locData,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token && !jobApplicantId) return;

    try {
      const missingAnswer = data?.questions.find((question) => {
        const value = answers[question.questionKey || ''];
        return value === undefined || value === null || value === '';
      });

      if (missingAnswer) {
        toast.error('Please answer all questions before submitting.');
        return;
      }

      const formattedAnswers = await Promise.all(
        Object.entries(answers).map(async ([key, value]) => {
          const loc = locationDataMap[key];
          const question = data?.questions.find((q) => q.questionKey === key);
          const isVideoQuestion = isVideoQuestionType(question?.questionType);
          const baseAnswer = {
            questionKey: key,
            value: isVideoQuestion ? '' : String(value),
          };

          if (isVideoQuestion) {
            const videoKey = await uploadVideoAndGetKey(key, value.file);
            return {
              ...baseAnswer,
              value: videoKey,
              videoKey,
            };
          }

          if (question?.questionType === 'location') {
            return {
              ...baseAnswer,
              location: loc
                ? {
                  city: loc.city,
                  state: loc.state,
                  country: loc.country,
                  postcode: loc.postcode,
                  lat: loc.coordinates.coordinates[1],
                  lng: loc.coordinates.coordinates[0],
                  unknown: false,
                }
                : {
                  city: '',
                  state: '',
                  country: '',
                  postcode: '',
                  lat: 0,
                  lng: 0,
                  unknown: true,
                },
            };
          }

          return baseAnswer;
        }),
      );

      if (token) {
        await submitPreQual({ token, answers: formattedAnswers }).unwrap();
      } else {
        await submitPublicPreQual({
          jobApplicantId,
          answers: formattedAnswers,
        }).unwrap();
      }
      setIsSubmitted(true);
      toast.success('Questionnaire submitted successfully!');
    } catch (err) {
      const message = getApiErrorMessage(err);
      toast.error(
        message || 'Failed to submit questionnaire. Please try again.',
      );
    }
  };

  if (!token && !jobApplicantId) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 p-4 font-sans'>
        <div className='bg-white p-8 rounded-2xl shadow-xl text-center max-w-md border border-gray-100'>
          <div className='w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6'>
            <svg
                className='w-8 h-8 text-red-500'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
              />
            </svg>
          </div>
          <h2 className='text-2xl font-bold text-gray-900 mb-2'>
            Invalid Link
          </h2>
          <p className='text-gray-600'>
            This link seems to be invalid or expired. Please check your email or
            contact support.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50'>
        <div className='flex flex-col items-center'>
          <div className='relative w-20 h-20'>
            <div className='absolute inset-0 border-4 border-gray-200 rounded-full'></div>
            <div className='absolute inset-0 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin'></div>
          </div>
          <p className='mt-6 text-gray-600 font-medium animate-pulse'>
            Loading questionnaire...
          </p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 p-4'>
        <div className='bg-white p-8 rounded-2xl shadow-xl text-center max-w-md border border-gray-100'>
          <div className='w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6'>
            <svg
                className='w-8 h-8 text-amber-500'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
          <h2 className='text-2xl font-bold text-gray-900 mb-2'>
            Error Loading
          </h2>
          <p className='text-gray-600'>
            We couldn&apos;t load your questionnaire. The link might have
            expired.
          </p>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 p-4'>
        <div className='bg-white p-8 rounded-2xl shadow-xl text-center max-w-md border border-gray-100'>
          <div className='w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner'>
            <svg
                className='w-8 h-8 text-green-500'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={3}
                  d='M5 13l4 4L19 7'
              />
            </svg>
          </div>
          <h2 className='text-2xl font-bold text-gray-900 mb-2'>
            Application Received
          </h2>
          <p className='text-gray-600 text-sm leading-relaxed'>
            Thank you for completing the questionnaire for the
            <span className='font-semibold text-emerald-600'>
              { ' ' }
              { data.jobTitle }
            </span>{ ' ' }
            position at
            <span className='font-semibold text-emerald-600'>
              { ' ' }
              { data.orgName }
            </span>
            .
          </p>
          { ccaEnabled && (
            <div className='mt-5 bg-blue-50 border border-blue-200 rounded-xl p-4 text-left space-y-1.5'>
              <div className='flex items-center gap-2 mb-1'>
                <div className='w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0'>
                  <svg
                      className='w-3 h-3 text-white'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2.5}
                        d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                    />
                  </svg>
                </div>
                <p className='text-xs font-black text-blue-800 uppercase tracking-wider'>
                  Next Step: Cognitive Assessment
                </p>
              </div>
              <p className='text-xs text-blue-700 leading-relaxed'>
                You will shortly receive an email with a link to a{ ' ' }
                <span className='font-bold'>Cognitive Assessment</span>. Please
                check your inbox (and spam/junk folder) and complete the
                assessment to finalise your application.
              </p>
            </div>
          ) }
          <div className='mt-8 pt-6 border-t border-gray-100'>
            <p className='text-xs text-gray-400'>
              Our hiring team will review your responses and get back to you
              soon.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50/50 selection:bg-emerald-100 font-sans'>
      { /* Header */ }
      <header className='bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm'>
        <div className='max-w-3xl mx-auto px-4 py-3 flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <img
                src={LogoWhite}
                alt='Logo'
                className='h-8 w-8 object-contain'
                onError={(e) => (e.currentTarget.style.display = 'none')}
            />
            <div className='h-6 w-[1px] bg-gray-200'></div>
            <div>
              <p className='text-[10px] font-black text-emerald-600 uppercase tracking-widest leading-none mb-0.5'>
                Candidate Portal
              </p>
              <h1 className='text-sm font-bold text-gray-900 leading-none'>
                { data.orgName }
              </h1>
            </div>
          </div>
          <div className='text-right'>
            <p className='text-[10px] text-gray-400 font-bold uppercase tracking-tighter'>
              Position
            </p>
            <p className='text-xs font-bold text-gray-700'>{ data.jobTitle }</p>
          </div>
        </div>
      </header>

      <main className='max-w-xl mx-auto px-4 py-8'>
        { /* Intro */ }
        <div className='mb-8'>
          <h2 className='text-2xl font-black text-gray-900 mb-2 tracking-tight'>
            Pre-Qualification Form
          </h2>
          <p className='text-gray-500 text-sm leading-relaxed'>
            Please answer the questions below to continue your application for
            the
            <span className='font-bold text-gray-700'>
              { ' ' }
              { data.jobTitle }
            </span>{ ' ' }
            role.
          </p>
          { ccaEnabled && (
            <div className='mt-4 flex gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3'>
              <div className='flex-shrink-0 w-1 bg-blue-400 rounded-full' />
              <p className='text-xs text-blue-700 leading-relaxed'>
                <span className='font-bold'>Heads up:</span> After submitting
                this form you will receive an email with a
                <span className='font-semibold italic'>
                  { ' ' }
                  Cognitive Assessment
                </span>{ ' ' }
                link. Check your inbox (and spam folder) and complete it to
                finalise your application.
              </p>
            </div>
          ) }
        </div>

        { /* Form */ }
        <form onSubmit={handleSubmit} className='space-y-6'>
          <div className='bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden divide-y divide-gray-50'>
            { data.questions.map((q, index) => (
              <div
                  key={q.questionKey || index}
                  className='p-6 hover:bg-gray-50/30 transition-colors'
              >
                <label className='block mb-4'>
                  <div className='flex items-start gap-3'>
                    <span className='flex-shrink-0 w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center text-xs font-bold mt-0.5 shadow-md shadow-emerald-100'>
                      { index + 1 }
                    </span>
                    <span className='text-base font-bold text-gray-800 leading-snug'>
                      { q.question }
                    </span>
                  </div>
                </label>

                <div className='pl-10'>
                  { q.questionType === 'number' && (
                    <div className='max-w-[200px]'>
                      <input
                          type='number'
                          required
                          min='0'
                          placeholder='Answer...'
                          value={answers[q.questionKey!] || ''}
                          onKeyDown={(e) => {
                            if (e.key === '-' || e.key === 'e') {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val !== '' && Number(val) < 0) return;
                            handleInputChange(q.questionKey!, val);
                          }}
                          className='w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 outline-none transition-all text-sm font-bold text-gray-900'
                      />
                    </div>
                  ) }

                  { q.questionType === 'multiple-choice' && (
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                      { q.choices.map((choice) => (
                        <label
                            key={choice}
                            className={`
                            relative flex items-center px-4 py-2.5 rounded-xl border cursor-pointer transition-all duration-200
                            ${
                              answers[q.questionKey!] === choice
                                ? 'border-emerald-600 bg-emerald-50 text-emerald-700 font-bold'
                                : 'border-gray-100 bg-white hover:border-emerald-200 text-gray-600'
                            }
                          `}
                        >
                          <input
                              type='radio'
                              name={q.questionKey}
                              value={choice}
                              required
                              checked={answers[q.questionKey!] === choice}
                              onChange={(e) =>
                                handleInputChange(q.questionKey!, e.target.value)
                              }
                              className='sr-only'
                          />
                          <span className='text-sm font-semibold'>
                            { choice }
                          </span>
                          { answers[q.questionKey!] === choice && (
                            <div className='ml-auto w-4 h-4 bg-emerald-600 rounded-full flex items-center justify-center'>
                              <svg
                                  className='w-3 h-3 text-white'
                                  fill='none'
                                  stroke='currentColor'
                                  viewBox='0 0 24 24'
                              >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={4}
                                    d='M5 13l4 4L19 7'
                                />
                              </svg>
                            </div>
                          ) }
                        </label>
                      )) }
                    </div>
                  ) }

                  { q.questionType === 'text' && (
                    <textarea
                        required
                        placeholder='Type your answer...'
                        value={answers[q.questionKey!] || ''}
                        onChange={(e) =>
                          handleInputChange(q.questionKey!, e.target.value)
                        }
                        className='w-full min-h-[120px] px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 outline-none transition-all text-sm font-medium text-gray-900 resize-y'
                    />
                  ) }

                  { q.questionType === 'location' && (
                    <div className='w-full'>
                      <LocationPicker
                          value={answers[q.questionKey!] || ''}
                          onChange={(val, data) =>
                            handleInputChange(q.questionKey!, val, data)
                          }
                      />
                    </div>
                  ) }

                  { isVideoQuestionType(q.questionType) && (
                    <VideoQuestionInput
                        value={answers[q.questionKey!] as VideoAnswerValue}
                        disabled={isSubmitting || isSubmittingPublic}
                        onChange={(value) =>
                          handleInputChange(q.questionKey!, value)
                        }
                    />
                  ) }
                </div>
              </div>
            )) }
          </div>

          <div className='pt-4'>
            <button
                type='submit'
                disabled={isSubmitting || isSubmittingPublic}
                className='group relative w-full overflow-hidden rounded-xl bg-emerald-600 p-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-100 transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed'
            >
              <div className='relative flex items-center justify-center gap-2'>
                { isSubmitting || isSubmittingPublic ? (
                  <>
                    <div className='w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin'></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span className='uppercase tracking-widest'>
                      Submit Answers
                    </span>
                    <svg
                        className='w-4 h-4 transition-transform group-hover:translate-x-1'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                    >
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={3}
                          d='M13 7l5 5m0 0l-5 5m5-5H6'
                      />
                    </svg>
                  </>
                ) }
              </div>
            </button>
            <p className='mt-6 text-center text-gray-400 text-xs font-bold uppercase tracking-tighter'>
              Progress: { Object.keys(answers).length } of { data.questions.length }{ ' ' }
              answered
            </p>
          </div>
        </form>
      </main>

      <footer className='py-12 text-center'>
        <p className='text-[10px] font-bold text-gray-300 tracking-widest uppercase opacity-60'>
          Powered by HiAcuity Intelligence
        </p>
      </footer>
    </div>
  );
};
